import React, { useState, useEffect } from 'react';
import { Users, User, Shield, Wrench, Gear, Eye, EyeSlash } from '@phosphor-icons/react';
import Cookies from 'js-cookie';

const ActiveUsers = ({ showToggle = true, sidebarOpen }) => {
  const [activeUsers, setActiveUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(!showToggle); // Si no hay toggle, siempre visible
  const [currentUser, setCurrentUser] = useState(null);


  // Obtener información del usuario actual
  useEffect(() => {
    const token = Cookies.get('sessionToken');

    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUser({
          id: payload.id,
          email: payload.email,
          role: payload.role
        });
      } catch (error) {
        console.error('Error decodificando token:', error);
      }
    }
  }, []);

  const updateUserActivity = async () => {
    try {
      // Registrar actividad del usuario actual
      const response = await fetch('/api/audit/update-activity', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          currentPage: window.location.pathname
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error updating user activity:', error);
    }
  };

  const fetchActiveUsers = async () => {
    try {
      // Primero actualizar la actividad del usuario actual
      await updateUserActivity();

      // Luego obtener la lista de usuarios activos
      const response = await fetch('/api/audit/active-users');

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const usersResponse = await response.json();
      setActiveUsers(usersResponse);
    } catch (error) {
      console.error('Error fetching active users:', error);
    } finally {
      setLoading(false);
    }
  };

  // Registrar actividad cuando el usuario navega
  useEffect(() => {
    if (currentUser) {
      // Registrar actividad inicial
      updateUserActivity();

      // Registrar actividad cada 30 segundos
      const activityInterval = setInterval(updateUserActivity, 30000);

      return () => clearInterval(activityInterval);
    }
  }, [currentUser]);

  useEffect(() => {
    if (isVisible) {
      fetchActiveUsers();

      // Actualizar cada 5 segundos para monitoreo en tiempo real
      const interval = setInterval(fetchActiveUsers, 5000);

      return () => clearInterval(interval);
    }
  }, [isVisible]);

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <Shield className="w-4 h-4 text-error" />;
      case 'operator':
        return <Gear className="w-4 h-4 text-info" />;
      case 'technician':
        return <User className="w-4 h-4 text-warning" />;
      default:
        return <User className="w-4 h-4 text-muted-deep" />;
    }
  };

  const getRoleName = (role) => {
    switch (role) {
      case 'admin':
        return 'Administrador';
      case 'operator':
        return 'Operador';
      case 'technician':
        return 'Técnico';
      default:
        return role;
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Ahora mismo';
    if (diffMins < 60) return `Hace ${diffMins} min`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `Hace ${diffHours}h ${diffMins % 60}min`;

    const diffDays = Math.floor(diffHours / 24);
    return `Hace ${diffDays}d ${diffHours % 24}h`;
  };

  const getUserStatus = (dateString, lastEndpoint) => {
    // Si el último endpoint fue logout, mostrar Desconectado
    if (lastEndpoint === '/logout' || lastEndpoint === '/api/audit/logout') {
      return 'Desconectado';
    }

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    return diffMins < 1 ? 'En línea' : 'Ausente';
  };

  // Filtrar usuarios excluyendo al usuario actual
  const currentUserEmail = currentUser?.email;
  const actualUser = currentUserEmail
    ? activeUsers.find(user => user.email === currentUserEmail)
    : null;
  const filteredUsers = currentUserEmail
    ? activeUsers.filter(user => user.email !== currentUserEmail)
    : [...activeUsers];

  if (actualUser) {
    filteredUsers.unshift(actualUser);
  }

  if (loading && isVisible) {
    if (!sidebarOpen) {
      return (
        <div className="flex flex-col items-center space-y-1">
          <div className="animate-pulse flex flex-col items-center space-y-1">
            <div className="w-10 h-10 bg-surface rounded"></div>
            <div className="w-8 h-6 bg-surface-2 rounded-md"></div>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-surface rounded-lg p-4">
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <div className="w-7 h-7 bg-surface-2 rounded-full"></div>
              <div className="h-4 w-32 bg-surface-2 rounded"></div>
            </div>
            {showToggle && <div className="h-7 w-20 bg-surface-2 rounded"></div>}
          </div>

          <div className="space-y-2">
            {[0, 1].map((index) => (
              <div key={`active-user-skeleton-${index}`} className="bg-surface-2 rounded p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-line rounded-full"></div>
                    <div className="space-y-2">
                      <div className="h-3 w-24 bg-line rounded"></div>
                      <div className="h-2 w-16 bg-line rounded"></div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end space-y-2">
                    <div className="h-3 w-16 bg-line rounded"></div>
                    <div className="h-2 w-12 bg-line rounded"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="h-9 w-full bg-surface-2 rounded mt-4"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {sidebarOpen ? (
        <div className="bg-surface rounded-lg">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Users className="w-7 h-7 text-content-muted" />
              <h3 className="text-content font-semibold">Usuarios en Línea</h3>
              {/*<span className="text-xs text-success bg-success/12 px-2 py-1 rounded">
                  {filteredUsers.length} conectados
                </span>*/}
            </div>
            {showToggle && (
              <button
                onClick={() => setIsVisible(!isVisible)}
                className="flex items-center space-x-1 bg-amber hover:bg-amber-hover text-on-amber text-xs px-3 py-1 rounded transition-colors"
              >
                {isVisible ? (
                  <>
                    <EyeSlash className="w-3 h-3" />
                    <span>Ocultar</span>
                  </>
                ) : (
                  <>
                    <Eye className="w-3 h-3" />
                    <span>Mostrar</span>
                  </>
                )}
              </button>
            )}

          </div>

          {/* Lista de usuarios activos (solo si está visible) */}
          {isVisible && (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {filteredUsers.length === 0 ? (
                <div className="text-center text-content-muted text-sm py-4">
                  No hay otros usuarios conectados en este momento
                </div>
              ) : (
                filteredUsers.map((user) => (
                  <div key={user.email} className="bg-surface-2 rounded p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {getRoleIcon(user.role)}
                        <div>
                          <div className="text-content text-sm font-medium">
                            {user.name}
                          </div>
                          <div className="text-xs text-content-muted">
                            {getRoleName(user.role)}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-xs font-semibold ${
                          getUserStatus(user.lastActivity, user.lastEndpoint) === 'En línea'
                            ? 'text-success'
                            : getUserStatus(user.lastActivity, user.lastEndpoint) === 'Desconectado'
                            ? 'text-error'
                            : 'text-warning'
                        }`}>
                          {getUserStatus(user.lastActivity, user.lastEndpoint)}
                        </div>
                        <div className="text-xs text-muted-deep">
                          {formatTime(user.lastActivity)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Botón de actualizar (solo si está visible) */}
          {isVisible && (
            <button
              onClick={fetchActiveUsers}
              className="mt-4 w-full bg-amber hover:bg-amber-hover text-on-amber text-sm py-2 px-4 rounded transition-colors"
            >
              Actualizar
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center space-y-1">
          <button
            onClick={() => setIsVisible(!isVisible)}
            className="w-10 h-10 flex items-center justify-center bg-surface rounded hover:bg-surface-2 transition-colors"
            title="Usuarios en línea"
          >
            <Users className="w-7 h-7 text-content-muted" />
          </button>
          <span className="text-normal text-success bg-success/12 px-2 py-1 rounded">
            {filteredUsers.length}
          </span>
        </div>
      )}
    </div>

  );
};

export default ActiveUsers;
